import React from 'react';
import PropTypes from 'prop-types';
import {
  CarouselProvider, Slider, Slide, ButtonBack, ButtonNext,
} from 'pure-react-carousel';
import { Icon, Link } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../../ui-kitchen/src/utils/sanitizeString';
import Chevron from '../../../../../images/icons/chevron-left.svg';
import { validateCarouselData, handleMktAnalytics } from '../../AmexUtils';
import { CTA_EVENT_NAME } from '../../constants';
import LayoutCarouselStaticSlide from './LayoutCarouselStaticSlide';
import './LayoutCarouselStatic.scss';

const LayoutCarouselStatic = (props) => {
  const {
    awsFolderName,
    baseRequestUrl,
    content: {
      sr_info: srInfo,
      sr_controls_aria: srControlsAria,
      carousel: carouselConfig,
      cta: {
        label: moreInfoLabel = '',
        url: moreInfoUrl = '',
      } = {},
    },
    Analytics,
    matchMedia,
    subLayout,
    tabHeading: heading,
  } = props;

  if (!carouselConfig || carouselConfig?.length === 0) return null;
  let filteredCarouselConfig;
  if (subLayout === 'shop') {
    const requiredCarouselKeys = ['image', 'copy', 'cta'];
    const requiredCtaKeys = ['label', 'url'];
    // filter carousel items based on required keys
    filteredCarouselConfig = validateCarouselData(carouselConfig, requiredCarouselKeys, requiredCtaKeys);
  } else {
    filteredCarouselConfig = carouselConfig;
  }

  if (filteredCarouselConfig.length === 0) return null;
  const carouselLength = filteredCarouselConfig?.length;
  const sanitizedHeading = sanitizeString(heading);

  const handleLinkClick = () => {
    const extraParamsAmplitude = {
      action_name: 'More Info Click',
      tab_item: heading && heading.toUpperCase(),
    };
    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParamsAmplitude);
  };

  const renderButtons = () => (
    <>
      <ButtonBack
        className="LayoutCarouselStatic__carousel__prev-button"
      >
        <span aria-hidden="true">
          <Icon
            name="Chevron"
            component={Chevron}
          />
        </span>
      </ButtonBack>
      <ButtonNext
        className="LayoutCarouselStatic__carousel__next-button"
      >
        <span aria-hidden="true">
          <Icon
            name="Chevron"
            component={Chevron}
          />
        </span>
      </ButtonNext>
    </>
  );

  const renderCarouselComponent = (item) => (
    <LayoutCarouselStaticSlide
      awsFolderName={awsFolderName}
      baseRequestUrl={baseRequestUrl}
      slideConfig={item}
      subLayout={subLayout}
      Analytics={Analytics}
      matchMedia={matchMedia}
      tabHeading={heading}
    />
  );

  const renderCarousel = () => (
    <div
      className="LayoutCarouselStatic__carousel"
      role="region"
    >
      <p className="sr-only">
        {srInfo}
      </p>
      <CarouselProvider
        visibleSlides={2}
        totalSlides={carouselLength}
        step={2}
        dragEnabled={false}
        naturalSlideWidth={400}
        naturalSlideHeight={650}
        isIntrinsicHeight="true"
        id="staticCarousel"
        aria-roledescription="carousel"
      >
        <div className="LayoutCarouselStatic__carousel__container">
          <div
            role="group"
            className="LayoutCarouselStatic__carousel__controls"
            aria-label={srControlsAria}
          >
            {carouselLength > 2 && renderButtons()}
          </div>
          <Slider>
            {filteredCarouselConfig.map((item, index) => (
              <Slide
                tabIndex={-1}
                index={index}
                key={item.copy}
                aria-label={`${index + 1} of ${carouselLength}`}
                aria-roledescription="slide"
              >
                {renderCarouselComponent(item)}
              </Slide>
            ))}
          </Slider>
        </div>
      </CarouselProvider>
    </div>
  );

  const renderGrid = () => (
    <div className="LayoutCarouselStatic__wrapper">
      <ul className="LayoutCarouselStatic__grid">
        {filteredCarouselConfig.map((item) => (
          <li key={item.copy}>
            {renderCarouselComponent(item)}
          </li>
        ))}
      </ul>
    </div>
  );

  const renderLayout = () => {
    if (carouselLength > 2) {
      if (matchMedia.md) return renderCarousel();
      return renderGrid();
    }
    return renderGrid();
  };

  return (
    <div className="LayoutCarouselStatic">
      {heading && (
        <h3
          className="LayoutCarouselStatic__heading  content-heading"
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: sanitizedHeading,
          }}
        />
      )}
      <div className="LayoutCarouselStatic__inner">
        {renderLayout()}
        {(moreInfoLabel && moreInfoUrl) && (
          <div className="LayoutCarouselStatic__bottom">
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href={moreInfoUrl}
              className="LayoutCarouselStatic__ctaLink"
              aria-label={`${heading} ${moreInfoLabel}`}
              onClick={() => handleLinkClick()}
            >
              {moreInfoLabel}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

LayoutCarouselStatic.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  content: PropTypes.shape({
    carousel: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    sr_info: PropTypes.string.isRequired,
    sr_controls_aria: PropTypes.string.isRequired,
    cta: PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  }).isRequired,
  subLayout: PropTypes.string,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  tabHeading: PropTypes.string,
};

LayoutCarouselStatic.defaultProps = {
  subLayout: '',
  tabHeading: '',
};

export default LayoutCarouselStatic;
